import React, { useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE_SECOND,
  DATE_FORMAT_MONTH_DAY,
  DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE,
} from '../../utils/dates';
import FeedFloDatePicker from '../../molecules/FeedFloDatePicker';
import { CalendarIcon } from '../../atoms/Icons';

const DEVICE_GQL = gql`
  query DeviceDataQuery($deviceId: uuid!, $start: bigint!, $end: bigint!) {
    device_data(
      where: { device_id: { _eq: $deviceId }, started_at: { _lte: $end }, ended_at: { _gte: $start } }
      order_by: { started_at: desc }
    ) {
      id
      id_from_device
      started_at
      ended_at
      created_at
      data
    }
  }
`;

export default function DeviceDataTable({ deviceId }) {
  // --- State hooks ---
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const now = dayjs.tz();
    return {
      from: now.subtract(1, 'hour'),
      to: now,
    };
  });

  // --- Derived state ---
  const startTimestamp = useMemo(() => dateRange.from.unix(), [dateRange.from]);
  const endTimestamp = useMemo(() => dateRange.to.unix(), [dateRange.to]);

  const {
    loading,
    error,
    data: deviceData,
  } = useQuery(DEVICE_GQL, {
    variables: {
      deviceId,
      start: startTimestamp,
      end: endTimestamp,
    },
  });

  // --- Function definitions ---
  const handleDateRangeChange = (e) => {
    if (e.dateRange) {
      setDateRange(e.dateRange);
      setShowDatePicker(false);
    }
  };

  const handleDatePickerClickOutside = () => {
    setShowDatePicker(false);
  };

  const isSameDay = dateRange.from.isSame(dateRange.to, 'day');
  const isFullDayRange =
    dateRange.from.hour() === 0 &&
    dateRange.from.minute() === 0 &&
    dateRange.from.second() === 0 &&
    dateRange.to.hour() === 23 &&
    dateRange.to.minute() === 59 &&
    dateRange.to.second() === 59;

  const fromLabel =
    isSameDay && !isFullDayRange
      ? dateRange.from.format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE)
      : dateRange.from.format(DATE_FORMAT_MONTH_DAY);
  const toLabel =
    isSameDay && !isFullDayRange
      ? dateRange.to.format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE)
      : dateRange.to.format(DATE_FORMAT_MONTH_DAY);

  if (loading) return <span>Loading</span>;
  if (error) return <span>{JSON.stringify(error, null, 2)}</span>;

  return (
    <div className="deviceDataHolder">
      <div style={{ marginBottom: '1rem', position: 'relative' }}>
        <div
          className="dateRangeButton"
          onClick={() => {
            setShowDatePicker(!showDatePicker);
          }}
        >
          <div className="icon">
            <CalendarIcon />
          </div>
          <div className="text">{`${fromLabel} - ${toLabel}`}</div>
        </div>
        {showDatePicker && (
          <div style={{ position: 'absolute', zIndex: 1000, top: '100%', left: 0, marginTop: '0.5rem' }}>
            <FeedFloDatePicker
              from={dateRange.from}
              to={dateRange.to}
              onChange={handleDateRangeChange}
              onClickedOutside={handleDatePickerClickOutside}
              maxDate={dayjs.tz()}
            />
          </div>
        )}
      </div>
      <table>
        <thead>
          <tr>
            <th>Device UUID</th>
            <th>Created At (DB)</th>
            <th>Started</th>
            <th>Ended</th>
            <th>Duration</th>
            <th>Size (Bytes)</th>
          </tr>
        </thead>
        <tbody>
          {deviceData?.device_data?.map((data) => {
            // Calculate size in bytes from hex string (each byte is 2 hex chars, minus "0x" prefix)
            const sizeInBytes = data.data ? (data.data.length - 2) / 2 : 0;

            return (
              <tr key={data.id}>
                <td>{data.id_from_device}</td>
                <td>{dayjs.tz(1000 * data.created_at).format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE_SECOND)}</td>
                <td>{dayjs.tz(1000 * data.started_at).format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE_SECOND)}</td>
                <td>
                  {data.ended_at
                    ? dayjs.tz(1000 * data.ended_at).format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE_SECOND)
                    : 'None'}
                </td>
                <td>{dayjs.tz(1000 * data.ended_at).from(data.started_at * 1000, true)}</td>
                <td>{sizeInBytes}</td>
                <td>
                  <Link target="_blank" to={`/tlv/${data.id}`}>
                    🔎
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

DeviceDataTable.propTypes = {
  deviceId: PropTypes.string,
};
